const MAX_YEARS_AHEAD = 8;
const DEFAULT_TARGET_MONTH = 12;
const DEFAULT_TARGET_DAY = 25;
const MAX_HIDDEN_RECHECK_MS = 60 * 60 * 1000;

function validTargetDate(year, targetMonth, targetDay) {
  if (!Number.isInteger(targetMonth) || targetMonth < 1 || targetMonth > 12) return null;
  if (!Number.isInteger(targetDay) || targetDay < 1 || targetDay > 31) return null;

  const target = new Date(year, targetMonth - 1, targetDay);
  if (
    target.getFullYear() !== year ||
    target.getMonth() !== targetMonth - 1 ||
    target.getDate() !== targetDay
  ) {
    return null;
  }

  return target;
}

export function calculateCountdown(
  now = new Date(),
  targetMonth = DEFAULT_TARGET_MONTH,
  targetDay = DEFAULT_TARGET_DAY,
) {
  // Visible year-round: always count down to the next occurrence of the target date.
  const invalid = { invalid: true, hidden: true, target: null, nextCheckAt: null, remainingMs: 0 };
  if (!Number.isInteger(targetMonth) || !Number.isInteger(targetDay)) return invalid;

  let target = null;
  for (let year = now.getFullYear(); year <= now.getFullYear() + MAX_YEARS_AHEAD; year += 1) {
    const candidate = validTargetDate(year, targetMonth, targetDay);
    if (candidate && candidate > now) {
      target = candidate;
      break;
    }
  }
  if (!target) return invalid;

  return {
    invalid: false,
    hidden: false,
    target,
    nextCheckAt: target,
    remainingMs: Math.max(0, target.getTime() - now.getTime()),
  };
}

export function getCountdownPresentation(countdown, designMode = false) {
  return {
    hidden: countdown.hidden && !designMode,
    preview: countdown.hidden && designMode,
  };
}

export function formatCountdown(remainingMs) {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value) => String(value).padStart(2, '0');

  return {
    days: pad(days),
    hours: pad(hours),
    minutes: pad(minutes),
    seconds: pad(seconds),
  };
}

export class CountdownTimer {
  constructor({
    tick,
    setTimeoutFn = globalThis.setTimeout,
    clearTimeoutFn = globalThis.clearTimeout,
  }) {
    this.tick = tick;
    this.setTimeoutFn = setTimeoutFn;
    this.clearTimeoutFn = clearTimeoutFn;
    this.timeout = null;
    this.running = false;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.scheduleNext();
  }

  scheduleNext = () => {
    if (!this.running) return;
    this.timeout = null;
    const state = this.tick() ?? {};
    const delay = state.hidden
      ? Math.min(Math.max(state.nextCheckMs ?? MAX_HIDDEN_RECHECK_MS, 1000), MAX_HIDDEN_RECHECK_MS)
      : 1000;
    // Call unbound: browsers throw "Illegal invocation" if setTimeout runs with `this` set to another object.
    const setTimeoutFn = this.setTimeoutFn;
    this.timeout = setTimeoutFn(this.scheduleNext, delay);
  };

  stop() {
    if (!this.running) return;
    this.running = false;
    const clearTimeoutFn = this.clearTimeoutFn;
    if (this.timeout !== null) clearTimeoutFn(this.timeout);
    this.timeout = null;
  }
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class EmCountdown extends HTMLElementBase {
  connectedCallback() {
    if (!this.timer) {
      this.timer = new CountdownTimer({ tick: () => this.update() });
    }
    this.timer.start();
  }

  disconnectedCallback() {
    this.timer?.stop();
  }

  restart() {
    this.timer?.stop();
    this.connectedCallback();
  }

  update() {
    const now = new Date();
    const targetMonth = Number.parseInt(this.dataset.targetMonth || DEFAULT_TARGET_MONTH, 10);
    const targetDay = Number.parseInt(this.dataset.targetDay || DEFAULT_TARGET_DAY, 10);
    const countdown = calculateCountdown(now, targetMonth, targetDay);
    const designMode = globalThis.Shopify?.designMode === true;
    const presentation = getCountdownPresentation(countdown, designMode);
    const section = this.closest('.shopify-section');

    this.hidden = presentation.hidden;
    this.toggleAttribute('data-editor-preview', presentation.preview);
    if (section) section.hidden = presentation.hidden;

    if (!countdown.hidden || presentation.preview) {
      const units = formatCountdown(countdown.remainingMs);
      this.updateUnit('[data-d]', units.days);
      this.updateUnit('[data-h]', units.hours);
      this.updateUnit('[data-m]', units.minutes);
      this.updateUnit('[data-s]', units.seconds);
    }

    return {
      hidden: countdown.hidden,
      nextCheckMs: countdown.nextCheckAt
        ? Math.max(1000, countdown.nextCheckAt.getTime() - now.getTime())
        : MAX_HIDDEN_RECHECK_MS,
    };
  }

  updateUnit(selector, value) {
    const output = this.querySelector(selector);
    if (output) output.textContent = value;
  }
}

if (typeof document !== 'undefined' && typeof customElements !== 'undefined') {
  if (!customElements.get('em-countdown')) {
    customElements.define('em-countdown', EmCountdown);
  }

  const editorListenerKey = '__emCountdownSectionListener';
  if (!globalThis[editorListenerKey]) {
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('em-countdown').forEach((countdown) => countdown.restart());
    });
    globalThis[editorListenerKey] = true;
  }
}
